import * as CollapsiblePrimitive from "@radix-ui/react-collapsible";
import { cn } from "~/lib/utils";

function Collapsible({ ...props }: React.ComponentProps<typeof CollapsiblePrimitive.Root>) {
  return <CollapsiblePrimitive.Root data-slot="collapsible" {...props} />;
}

function CollapsibleTrigger({
  ...props
}: React.ComponentProps<typeof CollapsiblePrimitive.CollapsibleTrigger>) {
  return <CollapsiblePrimitive.CollapsibleTrigger data-slot="collapsible-trigger" {...props} />;
}

export interface CollapsibleContentProps
  extends React.ComponentProps<typeof CollapsiblePrimitive.CollapsibleContent> {
  /**
   * If the animation is enabled the root of the Content component should not have padding or margin as these will cause visual bugs during the animation.
   *
   * @example
   * ```tsx
   * // Un-animated content does not need another wrapper div
   * <CollapsibleContent className="p-4 m-2">
   *   Content without animation
   * </CollapsibleContent>
   * ```
   *
   * @example
   * ```tsx
   * // Animated content should not have padding or margin on the root element
   * <CollapsibleContent animated>
   *   <div className="p-4 m-2">Content with animation</div>
   * </CollapsibleContent>
   * ```
   */
  animated?: boolean;
}

function CollapsibleContent({ className, animated, ...props }: CollapsibleContentProps) {
  return (
    <CollapsiblePrimitive.CollapsibleContent
      data-slot="collapsible-content"
      className={cn(
        animated &&
          "overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down",
        className
      )}
      {...props}
    />
  );
}

export { Collapsible, CollapsibleTrigger, CollapsibleContent };
